import { useMutation } from '@apollo/client'
import {
  createReportWithVictim,
  createReportWithVictimKey,
  encryptFileWithReportKey,
  serializePin,
} from '@faceup/crypto'
import { useNotification } from '@faceup/ui-base'
import { FormItemType } from '@faceup/utils'
import { useCallback, useContext, useState } from 'react'
import { graphql } from '../../../__generated__'
import { buildRecordingFile, isRecordType } from '../../../Components/VoiceRecording/buildRecording'
import type { RecordType } from '../../../Components/VoiceRecording/types'
import { ReportFormContext, ReportInputType } from '../../../Contexts/ReportFormContext'
import { UserContext } from '../../../Contexts/UserContext'
import { sharedMessages } from '../../../Shared/translations'
import { useIntl } from '../../../TypedIntl'
import useAnalytics from '../../../utils/analytics'
import useReportAuth from '../../../utils/useReportAuth'
import type { Recipient } from './useRecipients'

const mutations = {
  AF_CreateReportSubmission: graphql(`
    mutation AF_CreateReportSubmissionMutation(
      $input: AF_CreateReportSubmissionInput!
    ) {
      AF_CreateReportSubmission(input: $input) {
        createdCase {
          id
          tag
        }
        victimIdentity
      }
    }
  `),
}

type FormValue = {
  type: string
  value: unknown
}

type FormValues = Record<string, FormValue | Recipient[] | unknown>

type UseAF_CreateReportSubmitProps = {
  formId: string
  publicKey: string
  isE2EE: boolean
  onSent: (pin: string | null, tag: string) => void
  confirmBothTextAndVoice: () => Promise<boolean>
}

export const useAF_CreateReportSubmit = ({
  formId,
  publicKey,
  isE2EE,
  onSent,
  confirmBothTextAndVoice,
}: UseAF_CreateReportSubmitProps) => {
  const { formatMessage } = useIntl()
  const notification = useNotification()
  const { logout } = useReportAuth()
  const { reportSource } = useContext(UserContext)
  const { reportInputType } = useContext(ReportFormContext)
  const { track } = useAnalytics()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [createReportSubmission] = useMutation(mutations.AF_CreateReportSubmission, {
    onError: e => {
      if (e.message === 'Wrong company password') {
        window.location.href = '/'
      }
      setIsSubmitting(false)
    },
  })

  const transformFormValuesToAnswers = useCallback(
    (
      values: FormValues,
      excludedItemIds: string[]
    ): {
      formItemId: string
      textValue?: string | null
      optionIds?: string[] | null
      matrixValues?: { questionId: string; optionIds: string[] }[] | null
      files?: File[] | null
    }[] => {
      const answers: {
        formItemId: string
        textValue?: string | null
        optionIds?: string[] | null
        matrixValues?: { questionId: string; optionIds: string[] }[] | null
        files?: File[] | null
      }[] = []

      for (const [key, formValue] of Object.entries(values)) {
        // Skip excluded items (category, organizational unit, recipients, attachments)
        if (excludedItemIds.includes(key) || key === 'recipients' || key === 'attachments') {
          continue
        }

        // Skip if not a form value object
        if (
          typeof formValue !== 'object' ||
          formValue === null ||
          !('type' in formValue) ||
          !('value' in formValue)
        ) {
          continue
        }

        const { type, value } = formValue as FormValue

        // Skip items without values
        if (value === undefined || value === null || value === '') {
          continue
        }

        const itemType = type as FormItemType

        switch (itemType) {
          case FormItemType.SimpleText:
          case FormItemType.MultilineText:
          case FormItemType.Email:
          case FormItemType.PhoneNumber:
          case FormItemType.Date:
          case FormItemType.SenderName:
          case FormItemType.Classroom:
          case FormItemType.Number:
            answers.push({
              formItemId: key,
              textValue: String(value),
            })
            break

          case FormItemType.MoreInformation:
            // Voice recording is handled separately as moreInfoAttachment
            // Text value is handled in the encrypted body
            if (reportInputType === ReportInputType.Text && typeof value === 'string') {
              answers.push({
                formItemId: key,
                textValue: value,
              })
            }
            break

          case FormItemType.Select:
          case FormItemType.Category:
          case FormItemType.OrganizationalUnit:
          case FormItemType.Scale:
          case FormItemType.Rating:
            if (typeof value === 'string') {
              answers.push({
                formItemId: key,
                optionIds: [value],
              })
            }
            break

          case FormItemType.MultiSelect:
            if (Array.isArray(value) && value.length > 0) {
              answers.push({
                formItemId: key,
                optionIds: value as string[],
              })
            }
            break

          case FormItemType.Matrix:
            if (typeof value === 'object' && value !== null) {
              const matrixValues = Object.entries(value as Record<string, string[]>).map(
                ([questionId, optionIds]) => ({
                  questionId,
                  optionIds: optionIds ?? [],
                })
              )
              answers.push({
                formItemId: key,
                matrixValues,
              })
            }
            break

          case FormItemType.File:
            if (Array.isArray(value)) {
              answers.push({
                formItemId: key,
                files: value as File[],
              })
            }
            break
        }
      }

      return answers
    },
    [reportInputType]
  )

  const submit = useCallback(
    async ({
      values,
      recipients,
      categoryId,
      organizationalUnitId,
      excludedItemIds,
      isAnonymous,
      isAnonymityEnabled,
      isAnonymityPreselected,
      isSchoolDefaultForm,
      moreInfoItemId,
      senderNameItemId,
      victimNameValue,
    }: {
      values: FormValues
      recipients: Recipient[]
      categoryId: string
      organizationalUnitId: string
      excludedItemIds: string[]
      isAnonymous: boolean
      isAnonymityEnabled: boolean
      isAnonymityPreselected: boolean
      isSchoolDefaultForm: boolean
      moreInfoItemId?: string
      senderNameItemId?: string
      victimNameValue?: string
    }) => {
      setIsSubmitting(true)

      try {
        // Extract moreInfo and victimName for the encrypted body
        let moreInfoText = ''
        let moreInfoRecording: RecordType | null = null

        if (moreInfoItemId && values[moreInfoItemId]) {
          const moreInfoValue = values[moreInfoItemId] as FormValue
          if (typeof moreInfoValue.value === 'string' && moreInfoValue.value.trim()) {
            moreInfoText = moreInfoValue.value
          }
          if (isRecordType(moreInfoValue.value)) {
            moreInfoRecording = moreInfoValue.value
          }
        }

        // Check if both text and voice are present and confirm with user
        const hasBothTextAndVoice = Boolean(moreInfoText) && Boolean(moreInfoRecording)
        if (hasBothTextAndVoice) {
          const isConfirmed = await confirmBothTextAndVoice()
          if (!isConfirmed) {
            setIsSubmitting(false)
            return false
          }
        }

        // Get victim name (sender name when not anonymous)
        let victimName = ''
        if (victimNameValue) {
          victimName = victimNameValue
        } else if (!isAnonymous && senderNameItemId && values[senderNameItemId]) {
          const senderNameValue = values[senderNameItemId] as FormValue
          if (typeof senderNameValue.value === 'string') {
            victimName = senderNameValue.value.trim()
          }
        }

        // Get attachments
        const attachmentsValue = values['attachments'] as { file: File }[] | undefined
        const attachmentFiles = attachmentsValue?.map(a => a.file) ?? []

        // Build more info attachment file if voice recording
        const moreInfoAttachment = moreInfoRecording
          ? buildRecordingFile(moreInfoRecording)
          : undefined

        // Create report key
        const reportKey = await createReportWithVictimKey()

        // Only selected recipients with a valid public key can be encrypted to;
        // keyless selections would fail at crypto_box_seal.
        const selectedRecipients = recipients.flatMap(r =>
          r.isSelected && r.publicKey ? [{ id: r.id, key: r.publicKey }] : []
        )

        // Encrypt the report
        const payload = await createReportWithVictim({
          body: {
            moreInfo: reportInputType === ReportInputType.Text ? moreInfoText : '',
            victimName: victimName || null,
            attachments: attachmentFiles,
            moreInfoAttachment,
          },
          recipients: selectedRecipients,
          systemPbk: isE2EE ? undefined : publicKey,
          reportKey,
        })

        if (payload.isErr()) {
          setIsSubmitting(false)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
            description: payload.error.message,
          })
          return false
        }

        const {
          victimPin,
          privateKeyEncrypted,
          passwordPrehash,
          recipientKeys,
          senderKey,
          body,
          bodyNonce,
          nonce,
          salt,
          publicKey: victimPublicKey,
        } = payload.value

        // Prepare recipient keys including restricted ones
        const restrictedRecipientKeys = recipients
          .filter(r => !r.isSelected)
          .map(r => ({
            id: r.id,
            key: '',
            restricted: true,
          }))

        // Transform form values to answers
        const answers = transformFormValuesToAnswers(values, excludedItemIds)

        const answersWithEncryptedFiles = await Promise.all(
          answers.map(async answer => {
            if (!answer.files?.length) {
              return answer
            }
            const encryptedFiles = await Promise.all(
              answer.files.map(async file => {
                const result = await encryptFileWithReportKey(file, reportKey)
                if (result.isErr()) {
                  throw new Error(`Failed to encrypt file: ${result.error.message}`)
                }
                return result.value
              })
            )
            return { ...answer, files: encryptedFiles }
          })
        )

        // Call the mutation
        const response = await createReportSubmission({
          variables: {
            input: {
              formId,
              categoryId,
              organizationalUnitId,
              password: reportSource?.password ?? '',
              body,
              bodyNonce,
              answers: answersWithEncryptedFiles,
              senderKey,
              recipientKeys: [...recipientKeys, ...restrictedRecipientKeys],
              privateKeyEncrypted,
              publicKey: victimPublicKey,
              passwordPrehash,
              nonce,
              salt,
            },
          },
        })

        if (response?.errors) {
          setIsSubmitting(false)
          return false
        }

        if (!response?.data?.AF_CreateReportSubmission) {
          setIsSubmitting(false)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
          })
          return false
        }

        const identity = response.data.AF_CreateReportSubmission.victimIdentity ?? ''
        const pin = serializePin(identity + victimPin)
        const tag = response.data.AF_CreateReportSubmission.createdCase?.tag ?? ''

        await logout()
        track(
          'Report created',
          isSchoolDefaultForm
            ? { isSchoolDefaultForm }
            : {
                isSchoolDefaultForm,
                isAnonymous,
                wasAnonymityPossible: isAnonymityEnabled,
                wasAnonymityPreselected: isAnonymityPreselected,
              }
        )
        onSent(pin, tag)
        setIsSubmitting(false)
        return true
      } catch (error) {
        setIsSubmitting(false)
        notification.error({
          message: formatMessage(sharedMessages.encryptionError),
          description: error instanceof Error ? error.message : 'Unknown error',
        })
        return false
      }
    },
    [
      confirmBothTextAndVoice,
      createReportSubmission,
      formId,
      formatMessage,
      isE2EE,
      logout,
      notification,
      onSent,
      publicKey,
      reportInputType,
      reportSource?.password,
      track,
      transformFormValuesToAnswers,
    ]
  )

  return {
    submit,
    isSubmitting,
  }
}
